
import PropTypes from 'prop-types';
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious
} from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";

/**
 * Reusable Table Pagination Component
 * @param {Object} pagination - The object returned by usePagination hook
 * @param {boolean} isMobile - Whether to show mobile "load more" view
 */
export default function TablePagination({ pagination, isMobile = false }) {
    const {
        currentPage,
        totalPages,
        totalItems,
        hasMore,
        loadMore,
        goToPage,
        nextPage,
        prevPage,
        pageSize
    } = pagination;

    if (totalItems === 0) return null;

    if (isMobile) {
        if (!hasMore) return null;
        return (
            <div className="flex justify-center p-4">
                <Button
                    variant="outline"
                    onClick={loadMore}
                    className="w-full max-w-xs"
                >
                    Load More
                </Button>
            </div>
        );
    }

    // Desktop Pagination Logic
    const renderPageLinks = () => {
        const links = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        if (startPage > 1) {
            links.push(
                <PaginationItem key="1">
                    <PaginationLink onClick={() => goToPage(1)}>1</PaginationLink>
                </PaginationItem>
            );
            if (startPage > 2) links.push(<PaginationEllipsis key="e1" />);
        }

        for (let i = startPage; i <= endPage; i++) {
            links.push(
                <PaginationItem key={i}>
                    <PaginationLink
                        isActive={currentPage === i}
                        onClick={() => goToPage(i)}
                    >
                        {i}
                    </PaginationLink>
                </PaginationItem>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) links.push(<PaginationEllipsis key="e2" />);
            links.push(
                <PaginationItem key={totalPages}>
                    <PaginationLink onClick={() => goToPage(totalPages)}>{totalPages}</PaginationLink>
                </PaginationItem>
            );
        }

        return links;
    };

    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    return (
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 px-2 py-4 border-t">
            <div className="text-sm text-muted-foreground">
                Showing <span className="font-medium">{startItem}</span> to <span className="font-medium">{endItem}</span> of <span className="font-medium">{totalItems}</span> items
            </div>
            <Pagination>
                <PaginationContent>
                    <PaginationItem>
                        <PaginationPrevious
                            onClick={prevPage}
                            className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                    </PaginationItem>

                    {renderPageLinks()}

                    <PaginationItem>
                        <PaginationNext
                            onClick={nextPage}
                            className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                    </PaginationItem>
                </PaginationContent>
            </Pagination>
        </div>
    );
}

TablePagination.propTypes = {
    pagination: PropTypes.shape({
        currentPage: PropTypes.number,
        totalPages: PropTypes.number,
        totalItems: PropTypes.number,
        hasMore: PropTypes.bool,
        loadMore: PropTypes.func,
        goToPage: PropTypes.func,
        nextPage: PropTypes.func,
        prevPage: PropTypes.func,
        pageSize: PropTypes.number
    }),
    isMobile: PropTypes.bool
};
